import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaMapMarkerAlt, FaHeart, FaRegHeart, FaStar, FaChevronLeft, FaChevronRight, FaArrowRight } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { getVisits, likeVisit } from "../../slice/visitSlice";
import { getMediaUrl } from "../../common/mediaUrl";

import heroImg from "../../Assets/images/uttarakhand.jpg";

// Helper function to render rating stars
const renderRatingStars = (rating) => {
  const fullStars = Math.floor(rating || 5);
  const hasHalf = (rating || 5) % 1 !== 0;
  
  return (
    <div className="flex items-center gap-0.5 text-orange-main">
      {[...Array(5)].map((_, i) => {
        if (i < fullStars) {
          return <FaStar key={i} className="text-[13px]" />;
        } else if (i === fullStars && hasHalf) {
          return <FaStar key={i} className="text-[13px] opacity-50" />;
        } else {
          return <FaStar key={i} className="text-[13px] opacity-20" />;
        }
      })}
    </div>
  );
};

// Extracted PlaceCard component to handle its own image slider state
const PlaceCard = ({ place, index, isLiked, toggleLike }) => {
  const navigate = useNavigate();
  const [imgIndex, setImgIndex] = useState(0);

  const images = (() => {
    try {
      return JSON.parse(place.images);
    } catch (e) {
      return [];
    }
  })();

  const nextImg = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setImgIndex((prev) => (prev + 1) % images.length);
  };

  const prevImg = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setImgIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      onClick={() => navigate(`/khajuraho/${place.id}`)}
      className="group cursor-pointer flex flex-col transition-all duration-300"
    >
      {/* Image Slider Container */}
      <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden mb-4 shadow-[0_4px_15px_rgba(0,0,0,0.05)]">
        
        {/* Images */}
        <div className="w-full h-full relative">
          <AnimatePresence initial={false}>
            <motion.img
              key={imgIndex}
              src={getMediaUrl(images[imgIndex])}
              alt={place.name}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </AnimatePresence>
        </div>

        {/* Slider Arrows (appear on hover) */}
        {images.length > 1 && (
          <>
            <button 
              onClick={prevImg} 
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 hover:bg-white hover:scale-110 shadow-md"
            >
              <FaChevronLeft className="text-gray-800 text-[10px] -ml-0.5" />
            </button>
            <button 
              onClick={nextImg} 
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 hover:bg-white hover:scale-110 shadow-md"
            >
              <FaChevronRight className="text-gray-800 text-[10px] -mr-0.5" />
            </button>

            {/* Slider Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
              {images.map((_, i) => (
                <div 
                  key={i} 
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 shadow-sm ${
                    i === imgIndex ? 'bg-white scale-125' : 'bg-white/60'
                  }`} 
                />
              ))}
            </div>
          </>
        )}
        
        {/* Minimalist Like Button */}
        <button 
          onClick={(e) => { e.stopPropagation(); toggleLike(place.id); }}
          className="absolute top-3 right-3 flex items-center justify-center w-9 h-9 bg-white rounded-full shadow-md hover:scale-110 transition-transform z-20"
        >
          <motion.div
            whileTap={{ scale: 0.8 }}
            animate={isLiked ? { scale: [1, 1.2, 1] } : {}}
          >
            {isLiked 
              ? <FaHeart className="text-red-500 text-[17px]" /> 
              : <FaRegHeart className="text-gray-800 text-[17px]" />
            }
          </motion.div>
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-col px-1">
        <h4 className="text-xl font-bold text-gray-900 mb-1.5 leading-tight group-hover:text-orange-main transition-colors line-clamp-1">
          {place.name}
        </h4>

        {/* TripAdvisor Style Rating */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-800">{place.rating || 5}</span>
          {renderRatingStars(place.rating)}
          <span className="text-sm text-gray-500 font-medium">
            ({place.counting || 0} Likes)
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const Khajuraho = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: visits, loading } = useSelector((state) => state.visit);
  const [likedPlaces, setLikedPlaces] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  useEffect(() => {
    dispatch(getVisits());
  }, [dispatch]);

  const totalPages = Math.ceil((visits?.length || 0) / itemsPerPage);
  const currentPlaces = (visits || []).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleLike = (id) => {
    if (!likedPlaces[id]) {
      dispatch(likeVisit(id));
      setLikedPlaces(prev => ({
        ...prev,
        [id]: true
      }));
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(p => p + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(p => p - 1);
  };

  return (
    <div className="min-h-screen bg-[#fbf9f6] pb-20 font-sans">
      
      {/* Hero Section */}
      <div className="relative h-[65vh] w-full bg-black flex items-center justify-center overflow-hidden">
        {/* Background Image & Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImg} 
            alt="Khajuraho" 
            className="w-full h-full object-cover opacity-70 scale-105 animate-[pulse_20s_ease-in-out_infinite]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center h-full">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-main/20 border border-orange-main/30 text-orange-main font-bold text-[10px] uppercase tracking-[0.15em] mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-main animate-pulse"></span>
              Heritage Destination
            </div>
            
            <h1 className="text-4xl md:text-5xl font-black text-white leading-[1.1] tracking-tight mb-4">
              Explore The <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-main to-yellow-400">
                Khajuraho
              </span> Glory
            </h1>
            
            <p className="text-base sm:text-lg text-gray-300 font-medium leading-relaxed max-w-xl mb-8">
              Immerse yourself in the timeless beauty of ancient temples, intricate sculptures, and breathtaking natural landscapes.
            </p>

            <div className="flex items-center gap-3">
              <a href="#attractions" className="bg-orange-main text-white px-6 py-3 rounded-full font-bold text-xs uppercase tracking-wider hover:bg-orange-light transition-colors shadow-lg shadow-orange-main/30 flex items-center gap-2">
                View Attractions <FaMapMarkerAlt />
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <div id="attractions" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 md:mt-24">
        
        {/* Title and Intro */}
        <div className="mb-10 text-center md:text-left">
          <h2 className="text-2xl sm:text-4xl font-black text-orange-main mb-2 tracking-tight">
            Places to Visit in Khajuraho
          </h2>
        </div>

        {/* Places Grid */}
        <motion.div 
          layout
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10"
        >
          {loading ? (
             <div className="col-span-full py-20 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-main"></div>
                <p className="mt-4 text-gray-500 font-medium">Discovering beautiful places...</p>
             </div>
          ) : (
            <AnimatePresence mode="popLayout">
              {currentPlaces.length > 0 ? currentPlaces.map((place, index) => (
                <PlaceCard 
                  key={place.id} 
                  place={place} 
                  index={index} 
                  isLiked={likedPlaces[place.id]} 
                  toggleLike={toggleLike}
                />
              )) : <div className="col-span-full py-20 text-center text-gray-400">No places found.</div>}
            </AnimatePresence>
          )}
        </motion.div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-16 border-t border-gray-200 pt-8">
            <button 
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                currentPage === 1 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white text-gray-800 shadow-sm hover:bg-orange-main hover:text-white border border-gray-200 hover:border-transparent'
              }`}
            >
              <FaChevronLeft className="text-xs" />
            </button>
            
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    currentPage === i + 1 
                      ? 'bg-orange-main w-6' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <button 
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                currentPage === totalPages 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white text-gray-800 shadow-sm hover:bg-orange-main hover:text-white border border-gray-200 hover:border-transparent'
              }`}
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default Khajuraho;
